import { Box, FlexBox, Icon, IconNames, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { Timer } from 'lib-shared-legacy-components';
import type { NotificationT } from 'lib-shared-legacy-types';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ConversationStatusIcon } from 'components/conversation/components/conversationStatusIcon';
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';

import markdownToHTML from '../../../../app/utils/markdownToHTML';

import styles from './notificationLayout.module.scss';

type NotificationLayoutProps = {
  notification: Partial<NotificationT> &
    Pick<NotificationT, 'title' | 'status'>;
};

export const NotificationLayout = ({
  notification,
}: NotificationLayoutProps): JSX.Element => {
  const {
    id,
    title,
    text,
    subText,
    icon,
    status,
    conversation,
    waitingTime,
    reason,
    type,
  } = notification;

  // Extract csid from ACW notification id (format: "acw_${csid}")
  const acwCsid = type === 'acw' && id ? id.replace('acw_', '') : null;

  const rootClass = classnames({
    [styles.root]: styles.root,
    [styles.rootSuccess]: status === 'success',
    [styles.rootWarning]: status === 'warning',
    [styles.rootDanger]: status === 'danger',
  });

  const fixedIconName = icon ? (icon.replace(/^(i-)/, '') as IconNames) : icon;

  return (
    <FlexBox className={rootClass}>
      {!conversation && fixedIconName && (
        <FlexBox
          alignItems="center"
          justifyContent="center"
          margin="0 300 0 0"
          className={styles.icon}
        >
          <Icon name={fixedIconName} size={24} />
        </FlexBox>
      )}

      <FlexBox>
        {conversation && (
          <Box
            className={classnames(styles.conversationStatus, {
              [styles.conversationStatusOpen]: conversation.status === 'open',
              [styles.conversationStatusFollowUp]:
                conversation.status === 'pending' ||
                conversation.status === 'awaitingpending',
            })}
          />
        )}
        <FlexBox flexDirection="column" justifyContent="center">
          <FlexBox className={styles.title}>
            {conversation && (
              <ConversationStatusIcon
                channel={conversation.channel}
                direction={conversation.direction}
                status={conversation.status}
                className={styles.conversationStatusIcon}
              />
            )}
            {title}
          </FlexBox>
          {text && (
            <Box className={styles.text}>
              {type === 'acw' && acwCsid ? (
                <Text.Paragraph>
                  {text} <Link to={`/conversation/${acwCsid}`}>#{acwCsid}</Link>
                </Text.Paragraph>
              ) : typeof text === 'string' ? (
                <SafeInnerHtmlSetter
                  element="div"
                  content={markdownToHTML(text)}
                />
              ) : (
                (text as ReactNode)
              )}

              {waitingTime && (
                <div className={styles.waitingTime}>
                  Waited <Timer start={true} startAt={waitingTime} />
                </div>
              )}
              {reason && (
                <div className={styles.reason}>
                  Reason: {notification.reason}
                </div>
              )}
            </Box>
          )}
          {subText && <Box className={styles.subText}>{subText}</Box>}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
};
